// Import main CSS file
import '../css/popup-styles.css';
export default class StrlPopup {
  constructor(element, popupData) {
    this.popup = element;
    this.popupData = popupData;
    this.focusHandler = null;
    this.triggerElement = null;
    this.inertSiblings = [];

    if (!this.popupData.popupId) {
      return;
    }

    this.initialize();
  }

  initialize() {
    if (
      this.getCookie(`stuurlui-popup-shown-${this.popupData.popupId}`) &&
      this.popupData.frequency !== "always"
    ) {
      return;
    }

    this.setupTriggers();
    this.setupClosing();
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  }

  setCookie(name, value) {
    let expires = "";
    const { frequency, frequencyCustom, frequencyUnit } = this.popupData;
    if (frequency === "always") {
      return;
    } else if (frequency === "session") {
      expires = "; expires=0";
    } else if (frequency === "custom") {
      const date = new Date();
      let time = parseInt(frequencyCustom, 10);

      switch (frequencyUnit) {
        case "days":
          time *= 24 * 60 * 60 * 1000;
          break;
        case "weeks":
          time *= 7 * 24 * 60 * 60 * 1000;
          break;
        case "months":
          time *= 30 * 24 * 60 * 60 * 1000; // Approximation
          break;
      }

      date.setTime(date.getTime() + time);
      expires = "; expires=" + date.toUTCString();
    }

    document.cookie = `${name}=${value || ""}${expires}; path=/`;
  }

  showPopup() {
    if (this.popup.classList.contains("popup-is-visible")) {
      return;
    }
    this.triggerElement = document.activeElement;
    this.popup.classList.add(
      "popup-is-visible",
      `animation-${this.popupData.animation}`
    );
    setTimeout(() => {
      this.popup.classList.add(`is-animating`);
    }, 10);
    this.setCookie(`stuurlui-popup-shown-${this.popupData.popupId}`, "true");

    if (this.popupData.trapFocus) {
      this.trapFocus();
    }
    this.setInitialFocus();
    this.hideBackgroundContent();
  }

  closePopup() {
    this.popup.classList.remove("is-animating");
    setTimeout(() => {
      this.popup.classList.remove("popup-is-visible");
    }, 300);

    if (this.popupData.trapFocus) {
      this.releaseFocus();
    }
    if (this.triggerElement) {
      this.triggerElement.focus();
    }
    this.showBackgroundContent();
  }

  setupTriggers() {
    switch (this.popupData.trigger) {
      case "immediate":
        this.showPopup();
        break;
      case "delay":
        setTimeout(() => this.showPopup(), this.popupData.triggerDelay * 1000);
        break;
      case "scroll":
        this.handleScrollTrigger();
        break;
      case "element":
        this.handleElementTrigger();
        break;
    }
  }

  handleScrollTrigger() {
    const onScroll = () => {
      const scrollPercent =
        (window.scrollY /
          (document.documentElement.scrollHeight - window.innerHeight)) *
        100;
      if (scrollPercent > this.popupData.triggerScroll) {
        this.showPopup();
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  handleElementTrigger() {
    if (this.popupData.triggerElement) {
      const targets = document.querySelectorAll(
        this.popupData.triggerElement +
          ",a[href='" +
          this.popupData.triggerElement +
          "']"
      );

      if (targets.length > 0) {
        targets.forEach((target) => {
          target.addEventListener("click", () => {
            this.showPopup();
          });
        });
      }
    }
  }

  setupClosing() {
    const closeButtons = this.popup.querySelectorAll(".stuurlui-popup-close");
    if (closeButtons) {
      closeButtons.forEach((button) => {
        button.addEventListener("click", () => this.closePopup());
      });
    }

    const overlay = this.popup.querySelector(".stuurlui-popup-overlay");
    if (overlay) {
      overlay.addEventListener("click", () => this.closePopup());
    }

    document.addEventListener("keyup", (e) => {
      if (e.key === "Escape") {
        this.closePopup();
      }
    });
  }

  setInitialFocus() {
    const focusableElements = this.popup.querySelectorAll(
      'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    if (firstElement) {
      firstElement.focus();
    }
  }

  trapFocus() {
    const focusableElements = this.popup.querySelectorAll(
      'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    this.focusHandler = (e) => {
      if (e.key !== "Tab") {
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    this.popup.addEventListener("keydown", this.focusHandler);
  }

  releaseFocus() {
    if (this.focusHandler) {
      this.popup.removeEventListener("keydown", this.focusHandler);
      this.focusHandler = null;
    }
  }

  hideBackgroundContent() {
    const siblings = Array.from(document.body.children);
    this.inertSiblings = siblings.filter((sibling) => {
      if (
        sibling === this.popup ||
        sibling.tagName === "SCRIPT" ||
        sibling.hasAttribute("aria-hidden")
      ) {
        return false;
      }
      sibling.setAttribute("aria-hidden", "true");
      return true;
    });
  }

  showBackgroundContent() {
    this.inertSiblings.forEach((sibling) => {
      sibling.removeAttribute("aria-hidden");
    });
    this.inertSiblings = [];
  }
}

// set data for dev access
window.StuurluiPopups = {
  version: "3.5.1",
  getAllPopups: function () {
    return [...(window.stuurlui_popup_instances || [])];
  },
  getPopupById: function (popupId) {
    if (!window.stuurlui_popup_instances) return null;
    return (
			// TODO:fix
      window.stuurlui_popup_instances.find(
        (p) => p.popupData.popupId == popupId
      ) || null
    );
  },
  showPopup: function (popupId) {
    const popup = this.getPopupById(popupId);
    if (popup) popup.showPopup();
  },

  closePopup: function (popupId) {
    const popup = this.getPopupById(popupId);
    if (popup) popup.closePopup();
  },
  closeAllPopups: function () {
    if (!window.stuurlui_popup_instances) return;
    window.stuurlui_popup_instances.forEach((popup) => popup.closePopup());
  },
};

window.addEventListener("load", () => {
  const allPopupConfigs = window.stuurlui_popup_configs || [];
  if (!Array.isArray(allPopupConfigs) || allPopupConfigs.length === 0) {
    return;
  }

  window.stuurlui_popup_instances = window.stuurlui_popup_instances || [];

  allPopupConfigs.forEach((popupConfig) => {
    const popupElement = document.getElementById(
      `stuurlui-popup-${popupConfig.popupId}`
    );

		if (popupElement) {
      const p = new StrlPopup(popupElement, popupConfig);
      window.stuurlui_popup_instances['"'+popupConfig.popupId+'"'] = p;
    }
  });

  // TODO: not fully working, need to fix
  // Gravity Forms non-ajax integration - Force open when form is submitted
  if (typeof jQuery !== "undefined") {
    jQuery(document).on("gform_confirmation_loaded", function (event, formId) {
      const formWrapper = document.getElementById(`gform_wrapper_${formId}`);
      if (!formWrapper) return;

      const popupElement = formWrapper.closest(".stuurlui-popup");
      if (!popupElement) return;

      const popupId = popupElement.id.replace("stuurlui-popup-", "");
      if (!popupId) return;

      // const popupInstance = window.stuurlui_popup_instances.find(
      //   (p) => p.popupData.popupId == popupId
      // );
			// TODO: fix
			const popupInstance =window.stuurlui_popup_instances[popupId];
      if (popupInstance) {
        popupInstance.showPopup();
      }
    });
  }
});
